// Rutter (kodbaserade). Sidkomponenterna ligger i pages/.
import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Outlet, redirect } from "@tanstack/react-router";
import type { AsyncRouteComponent } from "@tanstack/react-router";
import { ErrorPage, NotFoundPage } from "./components/RouteErrors.tsx";
import { RouteFocus } from "./components/RouteFocus.tsx";
import { queryClient } from "./lib/query.ts";
import type { Me } from "./lib/types.ts";
import { api } from "./lib/api.ts";
import { LandingPage } from "./pages/Landing.tsx";

// Sidorna laddas först när de behövs, så att startsidan och inloggningen inte väntar på hela appen.
// Startsidan är med direkt. Inne i appen hämtas resten i bakgrunden (preloadPages), så att appen går att öppna utan nät.
const pages: { preload?: () => Promise<void> | undefined }[] = [];
function page<T extends Record<string, unknown>>(load: () => Promise<T>, name: keyof T & string) {
  const c = lazyRouteComponent(load, name);
  pages.push(c);
  return c;
}
const OrgShell = page(() => import("./components/Layout.tsx"), "OrgShell");
const ProjectShell = page(() => import("./components/Layout.tsx"), "ProjectShell");
const AnalyticsPage = page(() => import("./pages/Analytics.tsx"), "AnalyticsPage");
const AccountPage = page(() => import("./pages/Account.tsx"), "AccountPage");
const AcceptInvitePage = page(() => import("./pages/Auth.tsx"), "AcceptInvitePage");
const ForgotPasswordPage = page(() => import("./pages/Auth.tsx"), "ForgotPasswordPage");
const LoginPage = page(() => import("./pages/Auth.tsx"), "LoginPage");
const RegisterPage = page(() => import("./pages/Auth.tsx"), "RegisterPage");
const ResetPasswordPage = page(() => import("./pages/Auth.tsx"), "ResetPasswordPage");
const VerifyEmailPage = page(() => import("./pages/Auth.tsx"), "VerifyEmailPage");
const CoveragePage = page(() => import("./pages/Coverage.tsx"), "CoveragePage");
const DefectDetailPage = page(() => import("./pages/DefectDetail.tsx"), "DefectDetailPage");
const DefectsPage = page(() => import("./pages/Defects.tsx"), "DefectsPage");
const ExploratoryPage = page(() => import("./pages/Exploratory.tsx"), "ExploratoryPage");
const ExploratorySessionPage = page(() => import("./pages/Exploratory.tsx"), "ExploratorySessionPage");
const ImportPage = page(() => import("./pages/Import.tsx"), "ImportPage");
const AccessibilityPage = page(() => import("./pages/Legal.tsx"), "AccessibilityPage");
const CookiesPage = page(() => import("./pages/Legal.tsx"), "CookiesPage");
const DpaPage = page(() => import("./pages/Legal.tsx"), "DpaPage");
const PrivacyPage = page(() => import("./pages/Legal.tsx"), "PrivacyPage");
const TermsPage = page(() => import("./pages/Legal.tsx"), "TermsPage");
const NeedDetailPage = page(() => import("./pages/NeedDetail.tsx"), "NeedDetailPage");
const NeedsPage = page(() => import("./pages/Needs.tsx"), "NeedsPage");
const OverviewPage = page(() => import("./pages/Overview.tsx"), "OverviewPage");
const ProjectsPage = page(() => import("./pages/Projects.tsx"), "ProjectsPage");
const PublicIntakePage = page(() => import("./pages/PublicIntake.tsx"), "PublicIntakePage");
const BaselinePrintPage = page(() => import("./pages/BaselinePrint.tsx"), "BaselinePrintPage");
const BaselinesPage = page(() => import("./pages/Baselines.tsx"), "BaselinesPage");
const ProcurementPage = page(() => import("./pages/Procurement.tsx"), "ProcurementPage");
const MindMapPage = page(() => import("./pages/MindMap.tsx"), "MindMapPage");
const SystemMapPage = page(() => import("./pages/SystemMap.tsx"), "SystemMapPage");
const CapabilitiesPage = page(() => import("./pages/Capabilities.tsx"), "CapabilitiesPage");
const ChromosomesPage = page(() => import("./pages/Chromosomes.tsx"), "ChromosomesPage");
const ReportsPage = page(() => import("./pages/Reports.tsx"), "ReportsPage");
const WorkshopFacilitatorPage = page(() => import("./pages/WorkshopFacilitator.tsx"), "WorkshopFacilitatorPage");
const WorkshopJoinPage = page(() => import("./pages/WorkshopJoin.tsx"), "WorkshopJoinPage");
const WorkshopsPage = page(() => import("./pages/Workshops.tsx"), "WorkshopsPage");
const RequirementDetailPage = page(() => import("./pages/RequirementDetail.tsx"), "RequirementDetailPage");
const RequirementsPage = page(() => import("./pages/Requirements.tsx"), "RequirementsPage");
const RunExecutePage = page(() => import("./pages/RunExecute.tsx"), "RunExecutePage");
const RunsPage = page(() => import("./pages/Runs.tsx"), "RunsPage");
const SettingsPage = page(() => import("./pages/Settings.tsx"), "SettingsPage");
const LogsPage = page(() => import("./pages/Logs.tsx"), "LogsPage");
const ProjectSettingsPage = page(() => import("./pages/ProjectSettings.tsx"), "ProjectSettingsPage");
const TestCaseDetailPage = page(() => import("./pages/TestCaseDetail.tsx"), "TestCaseDetailPage");
const TestCasesPage = page(() => import("./pages/TestCases.tsx"), "TestCasesPage");
const TestPlanDetailPage = page(() => import("./pages/TestPlanDetail.tsx"), "TestPlanDetailPage");
const TestPlansPage = page(() => import("./pages/TestPlans.tsx"), "TestPlansPage");

/** Hämtar alla sidor i bakgrunden när webbläsaren har tid. Anropas när en inloggad öppnar appen. */
let preloaded = false;
export function preloadPages(): void {
  if (preloaded) return;
  preloaded = true;
  const run = () => {
    for (const c of pages) c.preload?.()?.catch(() => {});
  };
  if ("requestIdleCallback" in window) window.requestIdleCallback(run, { timeout: 5000 });
  else setTimeout(run, 2000);
}

// Roten: sidorna, och fokus och titel vid sidbyte (components/RouteFocus.tsx).
const root = createRootRoute({
  component: () => (
    <>
      <Outlet />
      <RouteFocus />
    </>
  ),
});

const home = createRoute({
  getParentRoute: () => root,
  path: "/",
  component: LandingPage,
  // Inloggade skickas till sin organisation. Utloggade (eller om servern inte svarar) ser marknadssidan.
  beforeLoad: async () => {
    let me: Me;
    try {
      me = await queryClient.fetchQuery({ queryKey: ["me"], queryFn: () => api.get<Me>("/me"), retry: false });
    } catch {
      return;
    }
    const org = me.orgs[0];
    if (!org) throw redirect({ to: "/skapa-konto" });
    throw redirect({ to: `/o/${org.id}` });
  },
});

const login = createRoute({ getParentRoute: () => root, path: "/logga-in", component: LoginPage });
const register = createRoute({ getParentRoute: () => root, path: "/skapa-konto", component: RegisterPage });
const forgot = createRoute({ getParentRoute: () => root, path: "/glomt-losenord", component: ForgotPasswordPage });
const reset = createRoute({ getParentRoute: () => root, path: "/aterstall/$token", component: ResetPasswordPage });
const invite = createRoute({ getParentRoute: () => root, path: "/inbjudan/$token", component: AcceptInvitePage });
const verifyEmail = createRoute({ getParentRoute: () => root, path: "/bekrafta-epost/$token", component: VerifyEmailPage });
const intake = createRoute({ getParentRoute: () => root, path: "/lamna-behov/$token", component: PublicIntakePage });
const workshopJoin = createRoute({ getParentRoute: () => root, path: "/w/$code", component: WorkshopJoinPage });
const legal = [
  createRoute({ getParentRoute: () => root, path: "/integritet", component: PrivacyPage }),
  createRoute({ getParentRoute: () => root, path: "/villkor", component: TermsPage }),
  createRoute({ getParentRoute: () => root, path: "/pub", component: DpaPage }),
  createRoute({ getParentRoute: () => root, path: "/cookies", component: CookiesPage }),
  createRoute({ getParentRoute: () => root, path: "/tillganglighet", component: AccessibilityPage }),
];

const org = createRoute({ getParentRoute: () => root, path: "/o/$orgId", component: OrgShell, onEnter: () => preloadPages() });
const orgIndex = createRoute({ getParentRoute: () => org, path: "/", component: ProjectsPage });
const settings = createRoute({ getParentRoute: () => org, path: "/installningar", component: SettingsPage });
const account = createRoute({ getParentRoute: () => org, path: "/konto", component: AccountPage });
const logs = createRoute({ getParentRoute: () => org, path: "/installningar/loggar", component: LogsPage });

const project = createRoute({ getParentRoute: () => org, path: "/p/$projectId", component: ProjectShell });
const p = (path: string, component: AsyncRouteComponent<unknown>) => createRoute({ getParentRoute: () => project, path, component });

const routeTree = root.addChildren([
  home,
  login,
  register,
  forgot,
  reset,
  invite,
  verifyEmail,
  intake,
  workshopJoin,
  ...legal,
  org.addChildren([
    orgIndex,
    settings,
    logs,
    account,
    project.addChildren([
      p("/", OverviewPage),
      p("/behov", NeedsPage),
      p("/behov/$id", NeedDetailPage),
      p("/krav", RequirementsPage),
      p("/krav/$id", RequirementDetailPage),
      p("/formagor", CapabilitiesPage),
      p("/tankekarta", MindMapPage),
      p("/projektkarta", SystemMapPage),
      // Det tidigare namnet, så att gamla länkar och bokmärken fungerar.
      p("/systemkarta", SystemMapPage),
      p("/testfall", TestCasesPage),
      p("/testfall/$id", TestCaseDetailPage),
      p("/testplaner", TestPlansPage),
      p("/testplaner/$id", TestPlanDetailPage),
      p("/korningar", RunsPage),
      p("/korningar/$id", RunExecutePage),
      p("/analys", AnalyticsPage),
      p("/utforska", ExploratoryPage),
      p("/utforska/$id", ExploratorySessionPage),
      p("/avvikelser", DefectsPage),
      p("/avvikelser/$id", DefectDetailPage),
      p("/tackning", CoveragePage),
      p("/kromosomkarta", ChromosomesPage),
      p("/import", ImportPage),
      p("/workshoppar", WorkshopsPage),
      p("/workshoppar/$id", WorkshopFacilitatorPage),
      p("/rapporter", ReportsPage),
      p("/baslinjer", BaselinesPage),
      p("/baslinjer/$id/utskrift", BaselinePrintPage),
      p("/installningar", ProjectSettingsPage),
      p("/upphandling", ProcurementPage),
    ]),
  ]),
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  scrollRestoration: true,
  // Ny post på samma rutt (till exempel ett annat testfall) ritar sidan från början, så att osparade utkast
  // och lokalt läge från förra posten aldrig följer med.
  defaultRemountDeps: ({ params }) => params,
  defaultNotFoundComponent: NotFoundPage,
  defaultErrorComponent: ErrorPage,
});
